import { useState, useMemo, useEffect } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetCommunityRequestsQuery,
  useUpdateRequestStatusMutation,
} from '@/services/CommunityManagementService';
import {
  ICommunityRequestData,
  RequestStatus,
} from '@/types/community-management.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { ConfirmationDialog } from '@/components/dialogBox';
import { enqueueSnackbar } from 'notistack';
import { CommunityRole } from '@/types/community-user.types';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

const RequestListings = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    userId: string | null;
    action: 'approve' | 'decline' | null;
    declineReason?: string;
    selectedRole?: CommunityRole;
  }>({
    isOpen: false,
    userId: null,
    action: null,
    selectedRole: CommunityRole.READER,
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );
  const { hasPermission } = usePermissions();

  const getStatusEnum = (filter: string): string | undefined => {
    const statusMap: { [key: string]: string } = {
      pending: RequestStatus.PENDING,
      approved: RequestStatus.APPROVED,
      declined: RequestStatus.DECLINED,
    };
    return filter !== 'all' ? statusMap[filter] : undefined;
  };

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    status: getStatusEnum(statusFilter),
    community_id: communityId || '',
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetCommunityRequestsQuery(queryParams, { skip: !communityId });

  const [updateRequestStatus, { isLoading: isUpdating }] =
    useUpdateRequestStatusMutation();

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (typeof refetch === 'function' && communityId) {
      refetch();
    }
  }, [statusFilter, debouncedValue, communityId, refetch]);
  const filteredData = useMemo(() => {
    if (!data) return [];
    const requests = Array.isArray(data) ? data : data.requests || [];
    return requests.map((request) => ({
      community_id: request.community_id,
      community_name: request.community_name,
      user_id: request.user_id,
      email: request.email,
      user_name: request.user?.name || 'Unknown',
      reason: request.reason || '-',
      status: request.status,
      created_at: request.created_at,
    }));
  }, [data]);

  const handleActionClick = (userId: string, action: 'approve' | 'decline') => {
    setConfirmActionState({
      isOpen: true,
      userId,
      action,
      selectedRole: CommunityRole.READER, // Default role
      declineReason: action === 'decline' ? '' : undefined,
    });
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setConfirmActionState((prev) => ({
      ...prev,
      selectedRole: e.target.value as CommunityRole,
    }));
  };

  const handleConfirmAction = async () => {
    if (confirmActionState.userId && confirmActionState.action && communityId) {
      const payload = {
        community_id: communityId,
        user_id: confirmActionState.userId,
        status:
          confirmActionState.action === 'approve'
            ? RequestStatus.APPROVED
            : RequestStatus.DECLINED,
        ...(confirmActionState.action === 'approve' && {
          role: confirmActionState.selectedRole,
        }),
        ...(confirmActionState.action === 'decline' && {
          decline_reason: confirmActionState.declineReason,
        }),
      };

      try {
        await updateRequestStatus(payload).unwrap();
        enqueueSnackbar(
          `User ${confirmActionState.action === 'approve' ? 'approved' : 'declined'} successfully`,
          { variant: 'success' }
        );
        if (typeof refetch === 'function') {
          refetch();
        }
        setConfirmActionState({
          isOpen: false,
          userId: null,
          action: null,
          declineReason: undefined,
        });
      } catch (error) {
        enqueueSnackbar(
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || 'Failed to update request status',
          { variant: 'error' }
        );
      }
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmActionState((prev) => ({
      ...prev,
      declineReason: e.target.value,
    }));
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: ICommunityRequestData) => row.user_name,
        id: 'user_name',
        header: 'User',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityRequestData } }) => {
          return (
            <span className="text-sm font-medium text-gray-900">
              {row.original.user_name && row.original.user_name !== 'Unknown'
                ? row.original.user_name
                : row.original.email}{' '}
            </span>
          );
        },
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: ICommunityRequestData) => row.reason,
        id: 'reason',
        header: 'Reason',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityRequestData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.reason}
          </span>
        ),
        meta: { className: 'min-w-[250px]' },
      },
      {
        accessor: (row: ICommunityRequestData) => row.created_at,
        id: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityRequestData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.created_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ICommunityRequestData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityRequestData } }) => (
          <span
            data-testid={`status-${row.original.user_id}`}
            className={`text-sm font-medium ${
              row.original.status === RequestStatus.APPROVED
                ? 'text-green-700 dark:text-green-600 contrast:text-green-600'
                : row.original.status === RequestStatus.DECLINED
                  ? 'text-red-600'
                  : 'text-yellow-700 dark:text-yellow-600 contrast:text-yellow-600'
            }`}
            role="status"
            aria-label={`Request status: ${row.original.status}`}
          >
            {row.original.status}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityRequestData } }) => (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Actions for ${row.original.user_name}'s request`}
          >
            {row.original.status === RequestStatus.PENDING &&
              hasPermission('community_management_update') && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      handleActionClick(row.original.user_id, 'approve')
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-green-500"
                    title="Approve request"
                    aria-label={`Approve ${row.original.user_name}'s request`}
                    disabled={isUpdating}
                  >
                    <KeenIcon
                      type="filled"
                      icon="check"
                      className="text-green-500"
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleActionClick(row.original.user_id, 'decline')
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500 contrast:text-white/50"
                    title="Decline request"
                    aria-label={`Decline ${row.original.user_name}'s request`}
                    disabled={isUpdating}
                  >
                    <KeenIcon
                      type="filled"
                      icon="cross"
                      className="text-red-500"
                      aria-hidden="true"
                    />
                  </button>
                </>
              )}
          </div>
        ),
        meta: { className: 'w-[150px]' },
      },
    ],
    []
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-requests"
              aria-label="Search requests"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-requests"
                placeholder="Search requests"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search requests by user or email"
                role="searchbox"
                disabled={isLoading || isFetching || isUpdating}
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter requests by status"
              disabled={isLoading || isFetching || isUpdating}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Filter controls"
        >
          {(searchTerm || statusFilter !== 'all') && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => {
                resetSearch();
                setStatusFilter('all');
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              aria-label="Clear all filters"
              title="Clear all filters"
              disabled={isLoading || isFetching || isUpdating}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || isUpdating ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
              aria-hidden="true"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-600"
            role="alert"
            aria-live="assertive"
          >
            Error loading data. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No requests available</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <DataGrid
                data-testid="requests-data-grid"
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching || isUpdating}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: data?.total || totalItems,
                  totalPages: data?.totalPages || totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'user_name', desc: false }]}
                serverSide={true}
                hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
                aria-label="Requests table"
              />
            )}
          </>
        )}
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              userId: null,
              action: null,
              declineReason: undefined,
              selectedRole: CommunityRole.READER,
            })
          }
          onConfirm={handleConfirmAction}
          message={
            <div className="space-y-4">
              <h2 className="text-md font-medium mb-2 contrast:text-white">
                {confirmActionState.action === 'approve'
                  ? 'Approve Request & Assign Role'
                  : 'Decline Request'}
              </h2>

              {confirmActionState.action === 'approve' ? (
                <>
                  <div
                    className="bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 p-3 rounded-lg"
                    role="note"
                    aria-label="Important information"
                  >
                    <p className="text-sm text-blue-700 dark:text-blue-300 contrast:text-blue-200">
                      <span className="font-medium">Note:</span> The user will
                      be notified of your decision. Please select the
                      appropriate role for this user.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="role-select"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Select Role
                      <span className="text-red-500 ml-1" aria-label="required">
                        *
                      </span>
                    </label>
                    <div className="relative">
                      <div className="relative">
                        <select
                          id="role-select"
                          className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md bg-light-active appearance-none"
                          value={confirmActionState.selectedRole}
                          onChange={handleRoleChange}
                          onFocus={() => setIsSelectOpen(true)}
                          onBlur={() => setIsSelectOpen(false)}
                          required
                          aria-required="true"
                          aria-label="Select role for user"
                        >
                          {Object.entries({
                            [CommunityRole.READER]: 'Reader - Can view content',
                            [CommunityRole.CONTRIBUTOR]:
                              'Contributor - Can create and edit content',
                            [CommunityRole.MODERATOR]:
                              'Moderator - Can manage content and users',
                          }).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <div
                          className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                          aria-hidden="true"
                        >
                          {isSelectOpen ? (
                            <svg
                              className="h-4 w-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="h-4 w-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : confirmActionState.action === 'decline' ? (
                <>
                  <div
                    className="bg-yellow-50 dark:bg-yellow-950 contrast:bg-yellow-950 p-3 rounded-lg"
                    role="note"
                    aria-label="Important information"
                  >
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 contrast:text-yellow-200">
                      <span className="font-medium">Note:</span> The user will
                      be notified that their request was declined. You may
                      provide an optional reason below.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="decline-reason"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Reason for Decline (optional)
                    </label>
                    <input
                      id="decline-reason"
                      type="text"
                      className="block w-full px-3 py-2 input border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={confirmActionState.declineReason || ''}
                      onChange={handleReasonChange}
                      placeholder="e.g., Community is currently at capacity"
                      aria-label="Reason for declining request"
                    />
                  </div>
                </>
              ) : null}
            </div>
          }
          cancelButtonText="Cancel"
          confirmButtonText={
            confirmActionState.action === 'approve'
              ? 'Approve & Assign Role'
              : 'Decline Request'
          }
          confirmButtonVariant={
            confirmActionState.action === 'approve' ? 'default' : 'warning'
          }
        />
      </div>
    </div>
  );
};

export default RequestListings;
